import { useContext, useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import axios from 'axios'

import BagIcon from '../../../assets/svgs/Bag'
import ChatIcon from '../../../assets/svgs/ChatIcon'
import HomeIcon from '../../../assets/svgs/Home'
import Icon from '../../../assets/svgs/Icon'
import SearchIcon from '../../../assets/svgs/Search'
import ShareIcon from '../../../assets/svgs/Share'
import UserIcon from '../../../assets/svgs/User'
import InfoIcon from '../../../assets/svgs/Info'

import style from './DashHeader.module.css'
import UploadModal from '../files/UploadModal'
import StartYourJourrney from '../../../utils/StartJourney'

import GuestContext from '../../../contexts/GuestContext'
import { useAuthContext } from '../../../contexts/AuthContext'
import { useCV } from '../../../contexts/CVContext'
import useWindowWidth from '../../../hooks/useWindowWidth'

// Only these tabs are enabled
const ALLOWED_TABS = new Set(['Home', 'Profile'])

const DashHeader = ({
  selectedTab,
  handleSelectTab,
  openGuestInfo,
  setOpenGuestInfo,
  openReg,
  setOpenAuth,
  handleAuth,
  user,
}) => {
  const location = useLocation()
  const [openUpload, setOpenUpload] = useState(false)
  const { setGuestUser } = useContext(GuestContext)
  const { isAuthenticated, logout, isSuperUser } = useAuthContext()
  const { isCVUploaded, setIsCVUploaded } = useCV()
  const [loading, setLoading] = useState(false)
  const innerWidth = useWindowWidth()

  const token = Cookies.get('token')
  const url = process.env.REACT_APP_API_URL || ''

  const toggleModal = () => setOpenGuestInfo(!openGuestInfo)

  const [openUserMenu, setOpenUserMenu] = useState(false)
  const userMenuRef = useRef(null)

  // Resources dropdown
  const [openResourcesMenu, setOpenResourcesMenu] = useState(false)
  const resourcesMenuRef = useRef(null)
  const navigate = useNavigate()

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setOpenUserMenu(false)
      }
      if (resourcesMenuRef.current && !resourcesMenuRef.current.contains(e.target)) {
        setOpenResourcesMenu(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenUserMenu(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    setOpenUserMenu(false)
    setOpenResourcesMenu(false)
  }, [location.pathname])

  const goToCVAnalyzer = () => {
    setOpenUserMenu(false)
    setOpenResourcesMenu(false)
    navigate('/dashboard/career-companion?id=' + (user?.user?.user_id || 'current'))
  }

  // Logout -> API + clear + external redirect
  const handleLogout = async (e) => {
    e?.preventDefault?.()
    try {
      await axios.get(`${url}/users/logout`, { withCredentials: true })
    } catch (error) {
      console.error('Error logging out:', error?.message || error)
    } finally {
      try {
        Cookies.remove('token')
        Cookies.remove('isCVUploaded')
      } catch { }
      logout?.()
      window.location.replace(process.env.REACT_APP_MAIN_SITE_URL || 'https://chimpknows.com/')
    }
  }

  // My profile
  const goToProfile = () => {
    setOpenUserMenu(false)
    handleSelectTab('Profile')
  }

  // Fetch user's files (for CV flag)
  useEffect(() => {
    const fetchUserFiles = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${url}/ai/user/get-files`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
        const { files } = response.data
        const hasCV = files.length > 0
        setIsCVUploaded(hasCV)
        Cookies.set('isCVUploaded', hasCV)
      } catch (error) {
        console.error('Error fetching user files:', error)
      } finally {
        setLoading(false)
      }
    }

    if (isAuthenticated) fetchUserFiles()
  }, [isAuthenticated])

  return (
    <>
      <div
        className={`${style.dashHeader} ${selectedTab === 'Chat' ? style.hidden : ''
          } ${location.pathname === '/dashboard/connect/create-new-event' &&
          style.hiddenHeader
          }`}
      >
        {/* LEFT: Logo */}
        <div className={style.icon} onClick={() => handleSelectTab('Home')}>
          <Icon width="40" height="40" color={'#432C5B'} />
          <span className={style.logoText}>CHIMP</span>
        </div>

        {/* CENTER: Navigation Pills */}
        {innerWidth > 768 && (
          <div className={style.menuItemsContainer}>
            {['Home', 'Resources', 'Spotlight', 'Connect', 'Roadmaps'].map((tab) => {


              const isResources = tab === 'Resources'
              const isResourcesActive = isResources && (openResourcesMenu || location.pathname.includes('career-companion'))

              if (isResources) {
                return (
                  <div
                    key={tab}
                    className={`${style.menuItem} ${style.userMenuWrapper} ${isResourcesActive ? style.highlightedTab : ''}`}
                    ref={resourcesMenuRef}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenResourcesMenu(!openResourcesMenu)
                    }}
                  >
                    Resources
                    {/* Tiny Chevron Down */}
                    <svg
                      width="10"
                      height="6"
                      viewBox="0 0 10 6"
                      fill="none"
                      style={{ marginLeft: 6, transform: openResourcesMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                    >
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>

                    {openResourcesMenu && (
                      <div className={style.userMenu} role="menu">
                        <button className={style.userMenuItem} onClick={goToCVAnalyzer}>
                          <span style={{ fontSize: '16px' }}>📄</span>
                          CV Analyzer
                        </button>
                      </div>
                    )}
                  </div>
                )
              }

              let isEnabled = ALLOWED_TABS.has(tab)
              // Spotlight specific check
              if (tab === 'Spotlight') {
                isEnabled = isAuthenticated && isSuperUser
              }
              const isActive = selectedTab === tab
              return (
                <div
                  key={tab}
                  className={`${style.menuItem} ${isActive ? style.highlightedTab : ''} ${!isEnabled ? style.disabled : ''}`}
                  onClick={() => {
                    if (isEnabled) handleSelectTab(tab)
                  }}
                  data-log-action={`nav_${tab.toLowerCase()}`}
                >
                  {tab}
                </div>
              )
            })}
          </div>
        )}

        {/* RIGHT: Actions (Start Journey + User Menu) */}
        <div className={style.rightActions}>
          {innerWidth > 768 && !isCVUploaded && !loading && (
            <StartYourJourrney
              handleAuth={handleAuth}
              openReg={openReg}
              setOpenAuth={setOpenAuth}
              user={user}
            />
          )}

          {!isAuthenticated && innerWidth > 768 && (
            <div className={style.userMenuWrapper}> {/* Wrap buttons for spacing */}
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('login')
                }}
                style={{ marginRight: 8, background: 'transparent', color: '#432c5b', boxShadow: 'none' }}
              >
                Log in
              </div>
              <div
                className={style.dashButton}
                onClick={() => {
                  setOpenGuestInfo(false)
                  handleAuth(!openReg)
                  setOpenAuth('register')
                }}
              >
                Sign up
              </div>
            </div>
          )}

          {/* User Profile Dropdown */}
          {isAuthenticated && innerWidth > 768 && (
            <div className={style.userMenuWrapper} ref={userMenuRef}>
              <div
                className={style.iconContainer}
                onClick={() => setOpenUserMenu((v) => !v)}
                role="button"
                aria-haspopup="menu"
                aria-expanded={openUserMenu}
                data-log-action="open_user_menu"
              >
                <UserIcon width={'18'} height={'18'} color={'#ffffff'} />
              </div>

              {openUserMenu && (
                <div className={style.userMenu} role="menu">
                  <button
                    className={style.userMenuItem}
                    onClick={goToProfile}
                    role="menuitem"
                  >
                    <UserIcon width={'16'} height={'16'} color={'#432C5B'} />
                    My profile
                  </button>
                  <button
                    className={style.userMenuItem}
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    {/* Logout Icon */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#432c5b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* MOBILE TOP BAR (Only visible on mobile via CSS media query) */}
        <div className={style.mobileMenuContainer}>


          {!isAuthenticated && (
            <div onClick={toggleModal} style={{ opacity: 0.6 }}>
              <InfoIcon width={'24px'} height={'24px'} />
            </div>
          )}

          {/* Render mobile user menu only on mobile */}
          {isAuthenticated && innerWidth <= 768 && (
            <div className={style.userMenuWrapper} ref={userMenuRef}>
              <div
                className={style.iconContainer}
                onClick={() => setOpenUserMenu((v) => !v)}
                style={{ width: 32, height: 32 }}
                data-log-action="open_user_menu"
              >
                <UserIcon width={'16'} height={'16'} color={'#ffffff'} />
              </div>

              {openUserMenu && (
                <div className={style.userMenu} role="menu">
                  <button
                    className={style.userMenuItem}
                    onClick={goToProfile}
                  >
                    My profile
                  </button>
                  <button
                    className={style.userMenuItem}
                    onClick={handleLogout}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Journey banner (unchanged) */}
      {innerWidth < 768 &&
        !location.pathname.startsWith('/dashboard/chat') &&
        !location.pathname.startsWith('/dashboard/connect/create-new-event') &&
        !isCVUploaded &&
        !loading && (
          <StartYourJourrney
            handleAuth={handleAuth}
            openReg={openReg}
            setOpenAuth={setOpenAuth}
          />
        )}

      {/* Bottom mobile nav (Profile removed) */}
      <div className={style.mobileMenu}>
        {['Home', 'Chat', 'Spotlight', 'Connect'].map((tab) => {
          const IconComponent =
            tab === 'Home'
              ? HomeIcon
              : tab === 'Chat'
                ? ChatIcon
                : tab === 'Spotlight'
                  ? BagIcon
                  : ShareIcon

          const isEnabled = ALLOWED_TABS.has(tab)

          return (
            <div
              key={tab}
              className={`${style.mobileMenuItem} ${isEnabled && selectedTab === tab ? style.highlighted : ''
                } ${!isEnabled ? style.disabled : ''}`}
              onClick={() => {
                if (isEnabled) handleSelectTab(tab)
              }}
              data-log-action={`nav_${tab.toLowerCase()}`}
            >
              <IconComponent
                stroke={isEnabled && selectedTab === tab ? '2' : '1'}
                color={isEnabled ? null : '#0000001A'}
              />
            </div>
          )
        })}
      </div>

      {openUpload && (
        <div className="cover">
          <UploadModal openUpload={openUpload} setOpenUpload={setOpenUpload} />
        </div>
      )}
    </>
  )
}

export default DashHeader
